/**
 * Legal-Statements — Zentrale Frontend-Konstanten der gesetzlichen Pflichthinweise.
 *
 * WICHTIG (Compliance, User-Auftrag Feb 2026):
 * Diese Texte MÜSSEN bit-identisch zur Backend-Single-Source-of-Truth sein:
 *   /app/backend/app/invoicing/statements.py  (DE_STATEMENTS, AT_STATEMENTS)
 *   /app/backend/app/invoicing/exports.py     (TAX_CATEGORY_CONFIG — XRechnung)
 *
 * Eine Diskrepanz zwischen UI-Vorschau und finalem PDF wäre ein
 * Compliance-Verstoß ("FL muss sehen, was wirklich auf dem PDF steht").
 *
 * Regressions-Test:  /app/backend/tests/test_legal_statement_compliance.py
 * (prüft die Identität gegen die hier definierten Strings).
 */

// =============================================================================
// DEUTSCHLAND (DE)
// =============================================================================

export const DE_LEGAL_STATEMENTS = {
  small_business: "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.",
  reverse_charge: "Steuerschuldnerschaft des Leistungsempfängers gemäß § 13b UStG.",
  intra_community:
    "Steuerfreie innergemeinschaftliche Lieferung gemäß § 4 Nr. 1b i.V.m. § 6a UStG.",
  export:
    "Steuerfreie Ausfuhrlieferung gemäß § 4 Nr. 1a i.V.m. § 6 UStG.",
  exempt: "Die Leistung ist gemäß § 4 UStG von der Umsatzsteuer befreit.",
};

// =============================================================================
// ÖSTERREICH (AT) — § 7 UStG 1994 für Export (NICHT Art. 7 — das wäre Binnenmarkt-Erwerb)
// =============================================================================

export const AT_LEGAL_STATEMENTS = {
  small_business: "Umsatzsteuerbefreit gemäß § 6 Abs. 1 Z 27 UStG 1994.",
  reverse_charge:
    "Steuerschuldnerschaft des Leistungsempfängers gemäß § 19 Abs. 1a UStG 1994.",
  intra_community:
    "Steuerfreie innergemeinschaftliche Lieferung gemäß Art. 6 Abs. 1 UStG 1994.",
  export: "Steuerfreie Ausfuhrlieferung gemäß § 7 UStG 1994.",
  exempt: "Die Leistung ist gemäß § 6 UStG 1994 von der Umsatzsteuer befreit.",
};

// =============================================================================
// SCHWEIZ (CH)
// =============================================================================

export const CH_LEGAL_STATEMENTS = {
  small_business: "Gemäß Art. 10 Abs. 2 MWSTG wird keine Mehrwertsteuer berechnet.",
  not_vat_registered: "Von der Mehrwertsteuer befreit.",
};

// =============================================================================
// Lookup-Helper
// =============================================================================

/**
 * Liefert den finalen Pflichthinweis-Text, der auf dem PDF erscheinen wird.
 *
 * @param {object} args
 * @param {string} args.country - "DE" | "AT" | "CH"
 * @param {string} args.scenario - "standard" | "reverse_charge" | "intra_community" | "export" | "small_business" | "exempt"
 * @param {boolean} args.isKleinunternehmer - true wenn KU-Status aktiv
 * @returns {{ text: string, legalRef: string } | null} - null = kein Pflichthinweis
 */
export function getLegalStatementForInvoice({ country, scenario, isKleinunternehmer }) {
  const c = (country || "DE").toUpperCase();

  // KU hat IMMER Vorrang — KU-Pflichthinweis ist auf dem PDF egal welches Szenario
  if (isKleinunternehmer) {
    if (c === "DE") return { text: DE_LEGAL_STATEMENTS.small_business, legalRef: "§ 19 UStG" };
    if (c === "AT") return { text: AT_LEGAL_STATEMENTS.small_business, legalRef: "§ 6 Abs. 1 Z 27 UStG 1994" };
    if (c === "CH") return { text: CH_LEGAL_STATEMENTS.small_business, legalRef: "Art. 10 Abs. 2 MWSTG" };
    return { text: DE_LEGAL_STATEMENTS.small_business, legalRef: "§ 19 UStG" };
  }

  // Cross-Border-Szenarien
  const sc = (scenario || "standard").toLowerCase();
  if (sc === "standard") return null;

  if (c === "AT") {
    const t = AT_LEGAL_STATEMENTS[sc];
    if (t) {
      const refMap = {
        reverse_charge: "§ 19 Abs. 1a UStG 1994",
        intra_community: "Art. 6 Abs. 1 UStG 1994",
        export: "§ 7 UStG 1994",
        exempt: "§ 6 UStG 1994",
      };
      return { text: t, legalRef: refMap[sc] };
    }
  }

  // Default: DE
  const t = DE_LEGAL_STATEMENTS[sc];
  if (t) {
    const refMap = {
      reverse_charge: "§ 13b UStG",
      intra_community: "§ 4 Nr. 1b, § 6a UStG",
      export: "§ 4 Nr. 1a, § 6 UStG",
      exempt: "§ 4 UStG",
    };
    return { text: t, legalRef: refMap[sc] };
  }

  return null;
}
